import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { generate_purchase_order, get_todays_purchase_orders, update_purchase_order, get_po_categories } from '../../../api/purchase_orders';
import EditPONumberModal from '../EditPONumberModal';
import logger from "../../../utils/logger";
import { redirect } from 'react-router-dom';


export default function PurchaseOrders({ theme = 'light' }) {
    const [comment, setComment] = useState('');
    const [supplier, setSupplier] = useState('');
    const [cost, setCost] = useState('');
    const [category, setCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [todaysPOs, setTodaysPOs] = useState([]);
    const [poCount, setPOCount] = useState(0);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editPOData, setEditPOData] = useState(null);
    
    const { showAlert } = useAlert();
    const { profile, permissions, isDev } = useAuthProvider();
    const canEdit = isDev || (permissions && permissions.perm_purchase_orders_edit);

    // Theme colors
    const colors = {
        light: {
            bg: '#ffffff',
            panel: '#f8f9fa',
            text: '#333333',
            border: '#dee2e6',
            input: '#ffffff',
            button: '#007bff',
            buttonHover: '#0056b3',
            red: '#dc3545'
        },
        dark: {
            bg: '#121212',
            panel: '#1e1e1e',
            text: '#ffffff',
            border: '#333333',
            input: '#2d2d2d',
            button: '#0d6efd',
            buttonHover: '#0b5ed7',
            red: '#ff4d4d'
        }
    };

    const color = colors[theme] || colors.light;

    const fetchCategories = async () => {
        try {
            const { data, status } = await get_po_categories();
            if (status === 200 && data.success) {
                // Sort categories alphabetically
                const sortedCategories = data.categories.sort((a, b) => a.name.localeCompare(b.name));
                setCategories(sortedCategories);
            }
        } catch (error) {
            logger.error('Error fetching categories:', error);
        }
    };

    // Fetch today's POs on component mount
    useEffect(() => {
        fetchTodaysPOs();
        fetchCategories();
    }, []);

    const fetchTodaysPOs = async () => {
        try {
            const { data, status } = await get_todays_purchase_orders();
            if (status === 200 && data.success) {
                setTodaysPOs(data.pos || []);
                setPOCount(data.count || 0);
            }
        } catch (error) {
            logger.error('Error fetching today\'s POs:', error);
        }
    };

    const handleGeneratePO = async () => {
        // Validate required fields
        if (!comment.trim() || !supplier.trim() || !category.trim()) {
            showAlert({
                message: 'Category, Supplier and Comment are required',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        // Validate cost format if provided
        if (cost.trim()) {
            const costRegex = /^\d+\.\d{2}$/;
            if (!costRegex.test(cost.trim())) {
                showAlert({
                    message: 'Cost must be in format 000.00',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
                return;
            }
        }

        if (!profile?.id) {
            showAlert({
                message: 'User profile not found',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        setIsLoading(true);

        try {
            const poData = {
                profile_id: profile.id,
                supplier: supplier.trim(),
                comment: comment.trim(),
                cost: cost.trim() || null,
                category: category.trim(),
                user_name: profile.name,
                user_surname: profile.surname
            };

            const { data, status } = await generate_purchase_order(poData);

            if (status === 200 && data.success) {
                showAlert({
                    message: data.message,
                    type: 'success',
                    buttons: [{ type: 'success', label: 'Close' }],
                });

                // Clear form
                setComment('');
                setSupplier('');
                setCost('');
                setCategory('');

                // Refresh today's POs
                fetchTodaysPOs();
            } else {
                showAlert({
                    message: data.error || 'Failed to generate purchase order',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error('Error generating PO:', error);
            showAlert({
                message: 'An error occurred while generating the purchase order',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (po) => {
        setEditPOData(po);
        setShowEditModal(true);
    };

    const handleEditSave = async (updatedPO) => {
        setShowEditModal(false);
        setEditPOData(null);
        setIsLoading(true);
        try {
            const { data, status } = await update_purchase_order(updatedPO);
            if (status === 200 && data.success) {
                showAlert({
                    message: data.message || 'PO updated successfully',
                    type: 'success',
                    buttons: [{ type: 'success', label: 'Close' }],
                });
                fetchTodaysPOs();
            } else {
                showAlert({
                    message: data.error || 'Failed to update PO',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            showAlert({
                message: 'An error occurred while updating the PO',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    };

    const formatCost = (costString) => {
        if (!costString) return '';
        try {
            const num = parseFloat(costString);
            return num.toFixed(2);
        } catch {
            return costString;
        }
    };

    const formatDateTime = (dateTimeString) => {
        try {
            const date = new Date(dateTimeString);
            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateTimeString;
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, rgba(0, 123, 255, 0.1) 0%, rgba(0, 123, 255, 0.05) 100%)`,
            padding: '20px',
            borderRadius: '10px'
        }}>
            <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                backgroundColor: color.panel,
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                border: `1px solid ${color.border}`
            }}>
                <h2 style={{
                    color: color.text,
                    textAlign: 'center',
                    marginBottom: '30px',
                    fontSize: '2rem',
                    fontWeight: 'bold'
                }}>
                    Purchase Orders
                </h2>

                {/* PO Generation Form */}
                <div style={{
                    backgroundColor: color.bg,
                    padding: '25px',
                    borderRadius: '10px',
                    marginBottom: '30px',
                    border: `1px solid ${color.border}`
                }}>
                    <h3 style={{
                        color: color.text,
                        marginBottom: '20px',
                        fontSize: '1.3rem'
                    }}>
                        Generate New Purchase Order
                    </h3>

                    {/* Category Field */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            color: color.text,
                            fontWeight: '500'
                        }}>
                            Category <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: `1px solid ${color.border}`,
                                backgroundColor: color.input,
                                color: color.text,
                                fontSize: '14px',
                                outline: 'none'
                            }}
                            disabled={isLoading}
                        >
                            <option value="">Select a category...</option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.name}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Comment Field */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            color: color.text,
                            fontWeight: '500'
                        }}>
                            Supplier <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <textarea
                            value={supplier}
                            onChange={(e) => setSupplier(e.target.value)}
                            placeholder="Enter the supplier for this purchase order..."
                            style={{
                                width: '100%',
                                minHeight: '10px',
                                padding: '10px',
                                borderRadius: '6px',
                                border: `1px solid ${color.border}`,
                                backgroundColor: color.input,
                                color: color.text,
                                fontSize: '14px',
                                resize: 'vertical',
                                outline: 'none'
                            }}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Comment Field */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            color: color.text,
                            fontWeight: '500'
                        }}>
                            Reason for PO <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Enter the reason for this purchase order..."
                            style={{
                                width: '100%',
                                minHeight: '100px',
                                padding: '12px',
                                borderRadius: '6px',
                                border: `1px solid ${color.border}`,
                                backgroundColor: color.input,
                                color: color.text,
                                fontSize: '14px',
                                resize: 'vertical',
                                outline: 'none'
                            }}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Cost Field */}
                    <div style={{ marginBottom: '25px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            color: color.text,
                            fontWeight: '500'
                        }}>
                            Cost (Optional)
                        </label>
                        <input
                            type="text"
                            value={cost}
                            onChange={(e) => setCost(e.target.value)}
                            placeholder="000.00"
                            pattern="^\d+\.\d{2}$"
                            title="Must be in format 000.00"
                            style={{
                                width: '200px',
                                padding: '12px',
                                borderRadius: '6px',
                                border: `1px solid ${color.border}`,
                                backgroundColor: color.input,
                                color: color.text,
                                fontSize: '14px',
                                outline: 'none'
                            }}
                            disabled={isLoading}
                        />
                        <small style={{
                            display: 'block',
                            marginTop: '5px',
                            color: color.text,
                            opacity: 0.7
                        }}>
                            Format: 000.00 (no pound sign)
                        </small>
                    </div>

                    {/* Generate Button */}
                    <button
                        onClick={handleGeneratePO}
                        disabled={isLoading || !comment.trim() || !supplier.trim() || !category.trim()}
                        style={{
                            backgroundColor: isLoading || !comment.trim() || !supplier.trim() || !category.trim() ? '#6c757d' : color.button,
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '12px 30px',
                            fontSize: '16px',
                            fontWeight: '500',
                            cursor: isLoading || !comment.trim() || !supplier.trim() || !category.trim() ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.2s',
                            ':hover': {
                                backgroundColor: !isLoading && comment.trim() && supplier.trim() && category.trim() ? color.buttonHover : undefined
                            }
                        }}
                    >
                        {isLoading ? 'Generating...' : 'Generate PO'}
                    </button>
                </div>

                {/* Today's POs */}
                <div style={{
                    backgroundColor: color.bg,
                    padding: '25px',
                    borderRadius: '10px',
                    border: `1px solid ${color.border}`
                }}>
                    <h3 style={{
                        color: color.text,
                        marginBottom: '20px',
                        fontSize: '1.3rem'
                    }}>
                        Today's Purchase Orders ({poCount})
                    </h3>

                    {todaysPOs.length > 0 ? (
                        <div style={{
                            display: 'grid',
                            gap: '15px'
                        }}>
                            {todaysPOs.map((po) => (
                                <div
                                    key={po.id}
                                    style={{
                                        backgroundColor: color.panel,
                                        padding: '20px',
                                        borderRadius: '8px',
                                        border: `1px solid ${color.border}`,
                                        borderLeft: '4px solid #007bff',
                                        position: 'relative'
                                    }}
                                >
                                    {canEdit && (
                                        <div style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 999 }}>
                                            <button
                                                onClick={() => handleEditClick(po)}
                                                style={{
                                                    background: '#ffc107',
                                                    color: '#222',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 12px',
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    )}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '10px'
                                    }}>
                                        <h4 style={{
                                            color: '#007bff',
                                            margin: '0',
                                            fontSize: '1.1rem',
                                            fontWeight: 'bold'
                                        }}>
                                            {po.po_number}
                                        </h4>
                                        {po.cost && (
                                            <span style={{
                                                backgroundColor: '#28a745',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}>
                                                £{formatCost(po.cost)}
                                            </span>
                                        )}
                                    </div>

                                    <p style={{
                                        color: color.red,
                                        margin: '0 0 10px 0',
                                        fontSize: '14px',
                                        lineHeight: '1.4'
                                    }}>
                                        {po.supplier}
                                    </p>
                                    
                                    {po.category && (
                                        <p style={{
                                            color: theme === 'light' ? '#0056b3' : '#4da6ff',
                                            margin: '0 0 10px 0',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            backgroundColor: theme === 'light' ? '#e3f2fd' : 'rgba(77, 166, 255, 0.2)',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            display: 'inline-block',
                                            border: `1px solid ${theme === 'light' ? '#bbdefb' : 'rgba(77, 166, 255, 0.3)'}`
                                        }}>
                                            {po.category}
                                        </p>
                                    )}
                                    
                                    <p style={{
                                        color: color.text,
                                        margin: '0 0 10px 0',
                                        fontSize: '14px',
                                        lineHeight: '1.4'
                                    }}>
                                        {po.comment}
                                    </p>
                                    
                                    <div style={{
                                        fontSize: '12px',
                                        color: color.text,
                                        opacity: 0.7
                                    }}>
                                        Created by: {po.created_by} at {formatDateTime(po.created_at)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: color.text,
                            opacity: 0.7
                        }}>
                            <p style={{ fontSize: '16px', margin: 0 }}>
                                No purchase orders generated today
                            </p>
                        </div>
                    )}
                    <EditPONumberModal
                        show={showEditModal}
                        onClose={() => { setShowEditModal(false); setEditPOData(null); }}
                        poData={editPOData}
                        onSave={handleEditSave}
                        theme={theme}
                    />
                </div>
            </div>
        </div>
    );
}
